import { memo, useRef, useMemo, useCallback, useEffect, ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Crud,
  CrudFormComponent,
  CrudMode,
  CrudProps,
  FormItem,
  getSelectsOptions,
  notification,
  SelectsOptions,
  ValidationError,
} from '@ohpen/enso-ui';
import {
  ActionEntityType,
  ActionType,
  ChangeRequestRowFeedback,
  ProductChangeDataRow,
  changeRequestCreateDraft,
  productChangesAddOrUpdateData,
  productChangesSubmitForReview,
} from '@pmp/api';
import { getNonEditableKeys, normalizeEntityValue, unflattenEntityCrudAdditionalFields } from '@pmp/components';
import { useFeatureToggle } from '@pmp/hooks';
import { changeRequestButtonMessage, changeRequestNotificationMessage } from '@pmp/utils';

import { getFormSchema } from '../formConfig';
import { ProductCrudData, ProductsCrudFormProps } from '../interfaces';
import { productsColumns } from '../tableConfig';
import { getAdditionalFieldsSchema } from '../utils';
import { ADDITIONAL_FIELDS_TAB, DETAILS_TAB, ProductsFormSchema } from './ProductsFormSchema';

const ROW_NUMBER = 0;

const HIDDEN_IN_CRUD_KEYS = productsColumns.filter(column => column.hideInCrud).map(column => String(column.dataIndex));

const toValidationErrors = (feedback: ChangeRequestRowFeedback | undefined): ValidationError[] =>
  Object.entries(feedback?.cellsFeedback ?? {})
    .filter(([, cell]) => cell.errors.length)
    .map(([dataIndex, cell]) => ({ name: [dataIndex], errors: cell.errors }));

const collectDataIndexes = (items: FormItem[]): string[] =>
  items.flatMap(item => {
    if (item.valueType === 'group') return collectDataIndexes((item.columns as FormItem[] | undefined) ?? []);
    return item.dataIndex === undefined ? [] : [String(item.dataIndex)];
  });

// Whitespace, '' and undefined all mean "still empty", so typing spaces into a field the server
// called required does not count as resolving it.
const asComparable = (value: unknown): string =>
  JSON.stringify(typeof value === 'string' ? value.trim() || null : (value ?? null));

export const ProductCrudForm = memo(
  ({ valueLists, additionalFields, refreshTable, onClose }: ProductsCrudFormProps): ReactElement => {
    const ref = useRef<CrudFormComponent>(null);
    const [open, setOpen] = useState(false);
    const [generalErrors, setGeneralErrors] = useState<string[]>([]);
    const [activeTab, setActiveTab] = useState(DETAILS_TAB);
    const { t } = useTranslation();
    const enableFourEyes = useFeatureToggle(['enableFourEyes']);

    const additionalFieldsKeys = useMemo(() => Object.keys(additionalFields ?? {}), [additionalFields]);

    const handleOnClose = useCallback(() => {
      setOpen(false);
      setTimeout(() => {
        onClose();
      }, 300);
    }, [onClose]);

    useEffect(() => {
      setOpen(true);
    }, []);

    // The draft is created on the first save, so a drawer closed without saving leaves no empty
    // change request behind. Later saves reuse it; a failed creation is retried on the next save.
    const draftRef = useRef<Promise<string> | null>(null);

    const getDraftActionId = useCallback((): Promise<string> => {
      draftRef.current ??= changeRequestCreateDraft({
        body: { actionEntityType: ActionEntityType.PRODUCTS, actionType: ActionType.CREATE },
      })
        .then(({ data }) => {
          if (!data.actionId) throw new Error('No actionId');
          return data.actionId;
        })
        .catch(error => {
          draftRef.current = null;
          throw error;
        });

      return draftRef.current;
    }, []);

    const schema: FormItem[] = useMemo(() => {
      const selectsOptions: SelectsOptions = (valueLists && getSelectsOptions(valueLists)) || {};

      return getFormSchema(selectsOptions, {
        excludedFields: [...getNonEditableKeys(ActionEntityType.PRODUCTS), ...HIDDEN_IN_CRUD_KEYS],
      });
    }, [valueLists]);

    const detailsFields = useMemo(() => new Set(collectDataIndexes(schema)), [schema]);

    const additionalFieldsSchema: FormItem | null = useMemo(() => {
      return getAdditionalFieldsSchema(additionalFields);
    }, [additionalFields]);

    // Server-side errors arrive through setFields and the fields carry no client-side rules, so
    // antd never revalidates them. Each one is kept with the value it was reported for and cleared
    // as soon as that field changes.
    const serverErrorsRef = useRef<Array<{ name: (string | number)[]; value: unknown }>>([]);

    const clearServerErrors = useCallback(() => {
      const form = ref.current?.form;
      if (form && serverErrorsRef.current.length) form.setFields(serverErrorsRef.current.map(({ name }) => ({ name, errors: [] })));
      serverErrorsRef.current = [];
    }, []);

    const clearResolvedServerErrors = useCallback(() => {
      const form = ref.current?.form;
      if (!form || !serverErrorsRef.current.length) return;

      const resolved: ValidationError[] = [];
      const unresolved: Array<{ name: (string | number)[]; value: unknown }> = [];
      for (const reported of serverErrorsRef.current) {
        if (asComparable(form.getFieldValue(reported.name)) === asComparable(reported.value)) {
          unresolved.push(reported);
        } else {
          resolved.push({ name: reported.name, errors: [] });
        }
      }

      if (!resolved.length) return;
      serverErrorsRef.current = unresolved;
      form.setFields(resolved);
    }, []);

    const onConfirm = useCallback(
      async (values: ProductCrudData): Promise<ValidationError[] | void> => {
        const actionId = await getDraftActionId();
        const code = normalizeEntityValue(ActionEntityType.PRODUCTS, 'code', values.code);
        const row = { ...values, code, rowNumber: ROW_NUMBER } as ProductCrudData;
        const data = unflattenEntityCrudAdditionalFields<ProductCrudData, ProductChangeDataRow>([row], additionalFieldsKeys);

        return productChangesAddOrUpdateData({ path: { actionId }, body: { data } })
          .then(({ data: validation }): Promise<ValidationError[] | void> | ValidationError[] => {
            const rowFeedback = validation.rowValidations[String(ROW_NUMBER)];

            clearServerErrors();

            if (!validation.isValid) {
              const isShown = ({ name }: ValidationError) =>
                detailsFields.has(String(name[0])) || additionalFieldsKeys.includes(String(name[0]));
              const allFieldErrors = toValidationErrors(rowFeedback);
              const fieldErrors = allFieldErrors.filter(isShown);
              // A field the drawer does not render has nowhere to show its error, so it is listed
              // with the general ones.
              const unshownErrors = allFieldErrors
                .filter(error => !isShown(error))
                .flatMap(({ name, errors }) =>
                  errors.map(error => `${t(`pmp.products.labels.${name[0]}`, { defaultValue: String(name[0]) })}: ${error}`),
                );
              const rowErrors = [...(rowFeedback?.errors ?? []), ...unshownErrors];
              const hasDetailsError = rowErrors.length > 0 || fieldErrors.some(({ name }) => detailsFields.has(String(name[0])));

              setGeneralErrors(rowErrors);
              setActiveTab(hasDetailsError || !fieldErrors.length ? DETAILS_TAB : ADDITIONAL_FIELDS_TAB);
              serverErrorsRef.current = fieldErrors.map(({ name }) => ({ name, value: ref.current?.form?.getFieldValue(name) }));

              return fieldErrors;
            }

            setGeneralErrors([]);

            return productChangesSubmitForReview({ path: { actionId } }).then(() => {
              notification().success({ title: changeRequestNotificationMessage(t, enableFourEyes) });
              if (!enableFourEyes) refreshTable();
            });
          });
      },
      [additionalFieldsKeys, clearServerErrors, detailsFields, enableFourEyes, getDraftActionId, refreshTable, t],
    );

    const crudProps: CrudProps<ProductCrudData> = useMemo(() => {
      return {
        ref,
        open,
        title: t('enso.labels.createX', { x: t('pmp.products.labels.product') }),
        labels: { confirm: changeRequestButtonMessage(t, enableFourEyes) },
        mode: CrudMode.CREATE,
        customForm: true,
        autoFocusFirstInput: true,
        width: 1100,
        disableDrawerBodyScroll: true,
        onClose: handleOnClose,
        onConfirm,
        onValuesChange: clearResolvedServerErrors,
      };
    }, [open, t, enableFourEyes, handleOnClose, onConfirm, clearResolvedServerErrors]);

    return (
      <Crud {...crudProps}>
        <ProductsFormSchema
          form={ref.current?.form}
          schema={schema}
          additionalFieldsSchema={additionalFieldsSchema}
          mode={CrudMode.CREATE}
          readonly={false}
          generalErrors={generalErrors}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      </Crud>
    );
  },
);
